import { useSeo } from "@/lib/seo";
import LegalLayout from "@/components/LegalLayout";
import { Trash2, Mail, Smartphone } from "lucide-react";

export default function DeleteAccount() {
    useSeo({
        title: "Suppression de compte — Coway",
        description:
            "Comment supprimer votre compte Coway et l'ensemble de vos données personnelles, en quelques étapes simples.",
        canonical: "https://coway.fr/delete-account",
    });

    return (
        <LegalLayout
            kicker="Compte"
            title="Suppression de votre compte"
            lastUpdated="1er février 2026"
            testId="delete-account-page"
        >
            <p>
                Vous pouvez à tout moment demander la suppression définitive de
                votre compte Coway et de l'ensemble des données associées. Deux
                méthodes sont possibles.
            </p>

            <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-10">
                <div className="p-6 rounded-3xl bg-white border border-slate-200 card-elevated">
                    <span className="inline-flex w-11 h-11 rounded-2xl bg-coway-soft border border-coway/15 items-center justify-center mb-5">
                        <Smartphone className="w-5 h-5 text-coway" strokeWidth={1.6} />
                    </span>
                    <h3 className="font-display text-xl font-bold text-slate-900">
                        Depuis l'application
                    </h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                        Ouvrez Coway → <strong className="text-slate-900">Profil</strong>
                        {" "}→ <strong className="text-slate-900">Paramètres</strong> →
                        {" "}<strong className="text-slate-900">Compte</strong> →
                        {" "}<strong className="text-slate-900">Supprimer mon compte</strong>.
                        Confirmez avec votre mot de passe.
                    </p>
                </div>
                <div className="p-6 rounded-3xl bg-white border border-slate-200 card-elevated">
                    <span className="inline-flex w-11 h-11 rounded-2xl bg-coway-soft border border-coway/15 items-center justify-center mb-5">
                        <Mail className="w-5 h-5 text-coway" strokeWidth={1.6} />
                    </span>
                    <h3 className="font-display text-xl font-bold text-slate-900">
                        Par email
                    </h3>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                        Envoyez votre demande à{" "}
                        <a
                            href="mailto:cowayfrance@gmail.com?subject=Suppression%20de%20compte%20Coway"
                            className="text-coway"
                            data-testid="delete-email-link"
                        >
                            cowayfrance@gmail.com
                        </a>{" "}
                        depuis l'adresse associée à votre compte, avec l'objet
                        « Suppression de compte ».
                    </p>
                </div>
            </div>

            <h2>Ce qui est supprimé</h2>
            <ul>
                <li>Votre profil (prénom, nom, photo, téléphone, email)</li>
                <li>Vos préférences et paramètres</li>
                <li>L'historique de vos messages</li>
                <li>Vos jetons d'authentification et sessions</li>
            </ul>

            <h2>Ce qui peut être conservé</h2>
            <p>
                Certaines données peuvent être conservées sous forme anonymisée
                ou archivée pour respecter nos obligations légales :
            </p>
            <ul>
                <li>
                    données comptables et de facturation (10 ans, obligation
                    légale française),
                </li>
                <li>
                    données nécessaires à la défense en justice ou à la
                    prévention de la fraude (3 ans après la dernière activité),
                </li>
                <li>
                    données agrégées et anonymisées utilisées à des fins
                    statistiques (qui ne permettent plus de vous identifier).
                </li>
            </ul>

            <h2>Délai de suppression</h2>
            <p>
                La suppression effective intervient sous{" "}
                <strong>30 jours maximum</strong> après votre demande. Pendant
                cette période, votre compte est désactivé et inaccessible.
            </p>

            <h2>Besoin d'aide ?</h2>
            <p>
                Notre équipe support reste à votre disposition à{" "}
                <a href="mailto:cowayfrance@gmail.com">cowayfrance@gmail.com</a>.
            </p>

            <div className="not-prose mt-12 p-6 rounded-3xl bg-coway-soft/40 border border-coway/20 flex items-start gap-4">
                <Trash2 className="w-5 h-5 text-coway mt-1 flex-shrink-0" strokeWidth={1.8} />
                <p className="text-sm text-slate-600 leading-relaxed">
                    <strong className="text-slate-900">Important :</strong> la
                    suppression est définitive. Une fois traitée, il ne sera
                    plus possible de récupérer votre historique ni vos
                    évaluations.
                </p>
            </div>
        </LegalLayout>
    );
}
